import {Button} from 'src/design/atoms/Button';
import {Anchor} from 'src/design/atoms/Anchor';
import {Breadcrumbs} from 'src/design/atoms/Breadcrumbs';
import {Divider} from 'src/design/atoms/Divider';
import {Flex} from 'src/design/atoms/Flex';
import {Text} from 'src/design/atoms/Text';
import {IconArrowLeft, IconChevronRight} from '@tabler/icons-react';
import React from 'react';
import {Link} from 'react-router-dom';
import {useAppSelector} from 'src/redux/hooks';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';

const ICON_SIZE_MD = 16;
const ICON_SIZE_SM = 14;

/**
 * BackBar - back button + breadcrumbs for detail pages.
 *
 * Usage:
 * ```tsx
 * <BackBar fallbackTo="/tasks" fallbackLabel="Back to Tasks" breadcrumbs={crumbs} />
 * <BackBar hideBackButton breadcrumbs={crumbs} />
 * ```
 *
 * Back returns to the page the user came from; `fallbackTo` is the static parent used
 * when there is none. The fallback label shows only when Back lands on that parent.
 */

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

interface BackBarBaseProps {
  /** Optional breadcrumb trail. Last item is current page (no link). */
  breadcrumbs?: BreadcrumbItem[];
  /** Content pinned to the right-side whitespace of the bar. */
  rightSection?: React.ReactNode;
}

interface BackBarWithBackProps extends BackBarBaseProps {
  /** Static parent page, used when there is no in-app origin to return to. */
  fallbackTo: string;
  /** Label shown when Back lands on `fallbackTo`; otherwise "Go Back". */
  fallbackLabel?: string;
  hideBackButton?: false;
}

interface BackBarWithoutBackProps extends BackBarBaseProps {
  hideBackButton: true;
  fallbackTo?: never;
  fallbackLabel?: never;
}

export type BackBarProps = BackBarWithBackProps | BackBarWithoutBackProps;

const GO_BACK_LABEL = 'Go Back';

export const BackButton: React.FC<{
  fallbackTo: string;
  fallbackLabel?: string;
}> = ({
  fallbackTo,
  fallbackLabel,
}) => {
  const {goBack, returnsToFallbackPage} = useBackNavigation(fallbackTo);
  return (
    <Button
      variant="subtle"
      color="black"
      size="sm"
      leftSection={<IconArrowLeft size={ICON_SIZE_MD} />}
      onClick={goBack}>
      {returnsToFallbackPage && fallbackLabel ? fallbackLabel : GO_BACK_LABEL}
    </Button>
  );
};

export const BackBar: React.FC<BackBarProps> = props => {
  const {breadcrumbs, rightSection} = props;
  const isHeadlessMode = useAppSelector(
    state => state.interface.isHeadlessMode
  );

  if (isHeadlessMode) {
    return null;
  }

  const hasBreadcrumbs = breadcrumbs && breadcrumbs.length > 0;
  const showBack = !props.hideBackButton;
  const showDivider = showBack && hasBreadcrumbs;

  // `mih` keeps a breadcrumb-only bar as tall as one with a back button, so pages
  // beside a fixed pane do not shift when the back target appears.
  return (
    <Flex align="center" gap="sm" wrap="wrap" mih={36} mb={{base: 'xs', lg: 0}}>
      {!props.hideBackButton && (
        <BackButton
          fallbackTo={props.fallbackTo}
          fallbackLabel={props.fallbackLabel}
        />
      )}

      {showDivider && (
        <Divider orientation="vertical" size="sm" h={20} visibleFrom="sm" />
      )}

      {/* Breadcrumbs */}
      {hasBreadcrumbs && (
        <Breadcrumbs
          separator={
            <IconChevronRight
              size={ICON_SIZE_SM}
              color="var(--mantine-color-gray-5)"
            />
          }
          separatorMargin={4}
          style={{flexWrap: 'wrap', rowGap: 4, lineHeight: 1.4, minWidth: 0}}>
          {breadcrumbs.map((crumb, index) => {
            const isLast = index === breadcrumbs.length - 1;

            if (isLast || !crumb.to) {
              return (
                <Text key={index} size="sm" c={isLast ? 'dark' : 'black'}>
                  {crumb.label}
                </Text>
              );
            }

            return (
              <Anchor
                key={index}
                component={Link}
                to={crumb.to}
                size="sm"
                c="dimmed">
                {crumb.label}
              </Anchor>
            );
          })}
        </Breadcrumbs>
      )}

      {rightSection && <div style={{marginLeft: 'auto'}}>{rightSection}</div>}
    </Flex>
  );
};
